<template>
  <div>
    <!-- H1 Goods Shipment Modal -->
    <edit-h1-parcel-items
      v-if="selectedParcel && declarationType === 'h1'"
      :id="'edit-h1-parcel-items-' + modalUid"
      :declaration="selectedDeclaration"
      :parcel="selectedParcel"
      :shipment="shipment"
      :readonly="!showEdit"
    />

    <!-- H7 Parcel Modal (default) -->
    <edit-parcel-items
      v-if="selectedParcel && declarationType !== 'h1'"
      :id="'edit-parcel-items-' + modalUid"
      :declaration="selectedDeclaration"
      :parcel="selectedParcel"
      :shipment="shipment"
      :readonly="!showEdit"
    />
    <b-row class="mt-1">
      <b-col
        v-if="isFilterAllowed('arrived')"
        cols="1"
        class="d-flex align-items-center h-100"
      >
        <div class="d-flex align-items-center">
          <b-form-checkbox
            id="arrived"
            v-model="arrived"
            switch
            class="me-2"
            @change="fetchContainersData"
          />
          <label
            for="arrived"
            class="mb-0"
          >
            Arrived
          </label>
        </div>
      </b-col>
      <b-col cols="3">
        <b-form-group
          label-for="buyer-country"
        >
          <b-form-select
            id="buyer-country"
            v-model="buyerCountry"
            label="Buyer country"
            :options="buyerCountryOptions"
            @change="applyCountryFilter"
          />
        </b-form-group>
      </b-col>
      <b-col>
        <div class="d-flex justify-content-between">
          <b-form-group
            class="full-width"
            label-for="container-codes"
          >
            <b-form-tags
              id="container-codes"
              v-model="filterContainerCodes"
              label="Container codes"
              :input-attrs="{ 'aria-describedby': 'tags-remove-on-delete-help' }"
              separator=" ,;"
              placeholder="Enter container codes separated by space, comma or semicolon"
              no-add-on-enter
              add-on-change
              clearable
              @input="fetchContainersData"
            />
          </b-form-group>
          <div>
            <b-button
              v-if="filtersApplied"
              id="filter-reset"
              variant="outline-secondary"
              class="ml-2"
              @click="resetFilters"
            >
              Reset
            </b-button>
          </div>
        </div>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-form-group
          label-for="parcel-tracking-number-tree"
        >
          <b-form-tags
            id="parcel-tracking-number-tree"
            v-model="filterContainerCodesByTrackingNumber"
            label="Parcel tracking numbers"
            :input-attrs="{ 'aria-describedby': 'tags-remove-on-delete-help' }"
            separator=" ,;"
            placeholder="Enter tracking numbers separated by space, comma or semicolon"
            no-add-on-enter
            add-on-change
            clearable
            @input="fetchContainersData"
          />
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group
          label-for="mrn-tree"
        >
          <b-form-tags
            id="mrn-tree"
            v-model="filterContainerCodesByMrn"
            label="MRN codes"
            :input-attrs="{ 'aria-describedby': 'tags-remove-on-delete-help' }"
            separator=" ,;"
            placeholder="Enter MRN codes separated by space, comma or semicolon"
            no-add-on-enter
            add-on-change
            clearable
            @input="fetchContainersData"
          />
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group
          label-for="lrn-tree"
        >
          <b-form-tags
            id="lrn-tree"
            v-model="filterContainerCodesByLrn"
            label="LRN codes"
            :input-attrs="{ 'aria-describedby': 'tags-remove-on-delete-help' }"
            separator=" ,;"
            placeholder="Enter LRN codes separated by space, comma or semicolon"
            no-add-on-enter
            add-on-change
            clearable
            @input="fetchContainersData"
          />
        </b-form-group>
      </b-col>
    </b-row>
    <b-table
      :items="flatTree"
      responsive
      :fields="fields"
      :sort-by.sync="sortBy"
      :sort-desc.sync="isSortDirDesc"
      no-local-sorting
      show-empty
      :busy="dataLoading"
      empty-text="No matching records found"
      class="mb-0 parcels-tree-table"
    >
      <template #table-busy>
        <div class="text-center my-2">
          <b-spinner class="align-middle mr-1" />
          <strong>{{ dataLoadingMessage }}...</strong>
        </div>
      </template>

      <template #head(container)="">
        <div
          v-if="canEdit"
          style="white-space: nowrap"
        >
          <b-form-checkbox
            v-if="!disableCheckbox"
            v-model="selectedAll"
            class="float-left select-all"
            @change="onChangeAll"
          />
          Container
          (<span
            v-if="selectedContainers.length > 0"
            class="selected-items"
          >{{ selectedContainers.length }} selected {{ selectedContainersKg }} kg / </span>{{ $attrs.containers.length }})
        </div>
      </template>

      <template #cell(container)="data">
        <div
          v-if="typeof data.item.tracking_number === 'undefined' && data.item.container"
          style="white-space: nowrap"
        >
          <b-form-checkbox
            v-if="!disableCheckbox && canEdit"
            v-model="selectedContainers"
            :value="data.item.container"
            class="float-left"
            @change="onChangeContainer(data.item.container)"
          />
          <b-link
            v-b-tooltip.hover.right="'Parcels in shown status / total container parcels'"
            href="#"
            class="containerLink"
            @click="openCloseParcels(data.item.container)"
          >
            <feather-icon
              icon="ChevronRightIcon"
              size="18"
              :class="{
                'chevron-icon': true,
                'expanded': isOpened(data.item.container)
              }"
            />
            <span v-if="data.item.container !== '(bulk)'">{{ data.item.container }}</span>
            <span
              v-if="data.item.container === '(bulk)'"
              style="font-style: italic;"
            >(bulk)</span>
            <span> ({{ data.item.goods_shipment_count }}/{{ data.item.total_goods_shipment_count }})</span>
          </b-link>
          <copy-icon
            :text="data.item.container"
            tooltip-text="Copy container code to clipboard"
            tooltip-success-text="Container code copied to clipboard"
          />
        </div>
      </template>

      <template #cell(buyer_country)="data">
        {{ data.item.buyer_country }}
      </template>
      <template #cell(tracking_number)="data">
        <div
          v-if="data.item.loading"
          class="text-center my-2"
        >
          <b-spinner class="align-middle mr-1" />
          <strong>Loading...</strong>
        </div>
        <div
          v-if="data.item.tracking_number"
          style="white-space: nowrap"
        >
          <b-form-checkbox
            v-if="!disableCheckbox && canEdit"
            v-model="selectedParcels"
            :value="data.item.tracking_number"
            class="float-left"
            @change="onChangeParcel(data.item.container)"
          />
          {{ data.item.tracking_number }}
        </div>
      </template>

      <template #cell(descriptions)="data">
        <div>
          {{ data.item.items ? truncatedDescriptions(data.item.items) : truncatedList(data.item.descriptions_preview) }}
        </div>
      </template>

      <template #cell(hs_codes)="data">
        <div>
          {{ data.item.items ? data.item.items.slice(0, 20).map(function (parcelItem) {return parcelItem.hs_code}).join(', ') : truncatedList(data.item.hs_codes_preview) }}
        </div>
      </template>

      <template #cell(value)="data">
        <div
          v-if="typeof data.item.tracking_number !== 'undefined'"
          class="text-right"
        >
          {{ parcelValue(data.item) | formatNumber(2) }}
        </div>
      </template>

      <template #cell(actions)="data">
        <div v-if="data.item.tracking_number">
          <b-button-group>
            <b-button
              v-if="!showEdit"
              type="button"
              class="parcel_view"
              variant="primary"
              size="sm"
              @click="openParcelModal(data.item)"
            >
              <b-icon-eye />
            </b-button>
            <b-button
              v-if="showEdit"
              type="button"
              variant="warning"
              @click="openParcelModal(data.item)"
            >
              <feather-icon icon="EditIcon" />
            </b-button>
            <b-button
              v-if="showEdit"
              type="button"
              variant="danger"
              @click="deleteParcel(data.item)"
            >
              <feather-icon icon="TrashIcon" />
            </b-button>
            <download-pdf-button
              v-if="!showEdit"
              :parcel="data.item"
              :shipment="shipment"
            />
          </b-button-group>
        </div>
      </template>
    </b-table>
  </div>
</template>

<script>
import {
  BFormCheckbox,
  BLink,
  BTable,
  BSpinner,
  BRow,
  BCol,
  BFormSelect,
  BFormGroup,
  BFormTags,
  BButton,
  BButtonGroup,
  BIconEye,
  VBTooltip,
} from 'bootstrap-vue'

import EditParcelItems from '@/views/shipments/declarations/modals/EditParcelItems.vue'
import EditH1GoodsItems from '@/views/shipments/declarations/modals/EditH1GoodsItems.vue'
import DownloadPdfButton from '@/views/shipments/declarations/components/DownloadPdfButton.vue'
import { EventBus, truncatedDescriptions, truncatedList, resolveGoodsShipmentValue } from '@core/utils/utils'
import { pushQueryIfChanged } from '@core/utils/filterQuerySync'
import CopyIcon from '@/views/components/CopyIcon.vue'

let modalCounter = 0
export default {
  components: {
    CopyIcon,
    DownloadPdfButton,
    BButtonGroup,
    EditParcelItems,
    EditH1ParcelItems: EditH1GoodsItems,
    BFormSelect,
    BCol,
    BRow,
    BFormCheckbox,
    BFormGroup,
    BLink,
    BTable,
    BSpinner,
    BFormTags,
    BButton,
    BIconEye,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    disableCheckbox: Boolean,
    dataLoading: {
      type: Boolean,
      default: false,
    },
    showEdit: {
      type: Boolean,
      default: false,
    },
    dataLoadingMessage: {
      type: String,
      default: 'Loading',
    },
    shipment: {
      type: Object,
      default: () => {},
    },
    filters: {
      type: Object,
      default: () => {},
    },
    declarationType: {
      type: String,
      default: 'h7',
    },
    notAllowedFilters: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    modalCounter += 1
    return {
      modalUid: modalCounter,
      loading: {},
      parcels: {},
      fullParcelsData: [],
      selectedDeclaration: null,
      selectedParcel: null,
      arrived: null,
      buyerCountry: '(All buyer countries)',
      defaultBuyerCountry: '(All buyer countries)',
      sortBy: 'container',
      isSortDirDesc: false,
      selectedAll: false,
      selectedContainers: [],
      selectedParcels: [],
      openContainers: [],
      filterContainerCodes: [],
      filterContainerCodesByCountry: [],
      filterContainerCodesByTrackingNumber: [],
      filterContainerCodesByMrn: [],
      filterContainerCodesByLrn: [],
    }
  },
  computed: {
    localFilters: {
      get() {
        return this.filters
      },
      set(newFilters) {
        this.$emit('updateFilters', newFilters)
      },
    },
    canEdit() {
      return this.$permissions().hasPermission('edit shipment')
    },
    filtersApplied() {
      return this.filterContainerCodes.length > 0
        || this.buyerCountry !== this.defaultBuyerCountry
        || this.filterContainerCodesByTrackingNumber.length > 0
        || this.filterContainerCodesByMrn.length > 0
        || this.filterContainerCodesByLrn.length > 0
        || this.arrived
    },
    selectedContainersKg() {
      const containers = this.containersForView
      let totalWeight = 0
      this.selectedContainers.forEach(containerCode => {
        if (containers[containerCode]) {
          totalWeight += containers[containerCode].total_goods_shipment_weight
        }
      })
      return totalWeight.toFixed(3)
    },
    buyerCountryOptions() {
      const { containersForView } = this
      const containers = Object.keys(containersForView)

      let countries = [this.defaultBuyerCountry]
      containers.forEach(containerKey => {
        const container = containersForView[containerKey]
        if (container.buyer_country) {
          countries = [...new Set([...countries, ...container.buyer_country.split(', ')])]
        }
      })
      return countries
    },
    fields() {
      let fields = [
        { key: 'container', label: 'Container', sortable: true },
        { key: 'buyer_country', label: 'Buyer country' },
        { key: 'tracking_number', label: 'Tracking code', sortable: this.openContainers.length > 0 },
        { key: 'descriptions', label: 'Description' },
        { key: 'hs_codes', label: 'HS code' },
        { key: 'value', label: 'Value' },
        { key: 'actions', label: 'Actions' },
      ]
      if (this.$attrs.clearance) {
        fields = [
          { key: 'container', label: 'Container', sortable: true },
          { key: 'tracking_number', label: 'Tracking code', sortable: this.openContainers.length > 0 },
          { key: 'lrn', label: 'LRN' },
          { key: 'mrn', label: 'MRN' },
          { key: 'actions', label: 'Actions' },
        ]
      }
      return fields
    },
    flatTree() {
      const { containersForView } = this
      const flatTree = []

      const containers = Object.keys(containersForView)

      containers.forEach(containerKey => {
        const container = containersForView[containerKey]
        if (this.buyerCountry !== this.defaultBuyerCountry && (!container.buyer_country || !container.buyer_country.includes(this.buyerCountry))) {
          return
        }
        const flatContainer = {
          id: container.code,
          container: container.code || '(bulk)',
          buyer_country: container.buyer_country,
          goods_shipment_count: container.goods_shipment_count,
          total_goods_shipment_count: container.total_goods_shipment_count,
          items: [],
        }
        flatTree.push(flatContainer)

        const openedIndex = this.openContainers.indexOf(container.code)

        if (openedIndex === -1) {
          return
        }

        const parcels = this.parcels[container.code]

        if (typeof parcels === 'undefined') {
          flatTree.push({
            loading: true,
            items: [],
          })
          if (this.loading[container.code] !== true) {
            this.loading[container.code] = true
            this.requestParcels(container.code)
          }
          return
        }

        for (let i = 0; i < parcels.length; i += 1) {
          const parcel = parcels[i]
          if (this.buyerCountry === this.defaultBuyerCountry || parcel.buyer.country === this.buyerCountry) {
            flatTree.push({
              ...parcel,
              buyer_country: parcel.buyer.country,
              container: container.code,
              lrn: parcel.low_value_declaration?.lrn,
              mrn: parcel.low_value_declaration?.mrn,
            })
          }
        }
      })

      return flatTree
    },
    containersForView() {
      const { containers } = this.$attrs
      const containersForView = {}

      for (let i = 0; i < containers.length; i += 1) {
        const container = containers[i]

        containersForView[container.code] = {
          code: container.code,
          buyer_country: container.buyer_country,
          goods_shipment_count: container.goods_shipment_count,
          total_goods_shipment_count: container.total_goods_shipment_count,
          total_goods_shipment_weight: container.total_goods_shipment_weight,
          parcels: null,
        }
      }

      return containersForView
    },
  },
  watch: {
    '$route.query': {
      handler(query) {
        // Keep the tree filter inputs (and the resulting backend request) in
        // sync with the URL. When the query changes externally (tab switch,
        // back/forward, filter cleared elsewhere) re-derive the filters from it.
        const q = query || {}
        const tracking = q.tracking_number ? q.tracking_number.split(',') : []
        const containers = q.container_code ? q.container_code.split(',') : []
        const mrn = q.mrn ? q.mrn.split(',') : []
        const lrn = q.lrn ? q.lrn.split(',') : []
        const changed = tracking.join(',') !== this.filterContainerCodesByTrackingNumber.join(',')
          || containers.join(',') !== this.filterContainerCodes.join(',')
          || mrn.join(',') !== this.filterContainerCodesByMrn.join(',')
          || lrn.join(',') !== this.filterContainerCodesByLrn.join(',')
        if (!changed) return
        this.filterContainerCodesByTrackingNumber = tracking
        this.filterContainerCodes = containers
        this.filterContainerCodesByMrn = mrn
        this.filterContainerCodesByLrn = lrn
        this.fetchContainersData()
      },
    },
    filters() {
      this.parcels = {}
    },
    sortBy() {
      if (this.sortBy !== 'container') {
        this.openContainers.forEach(container => {
          this.requestParcels(container)
        })
      }
    },
    isSortDirDesc() {
      if (this.sortBy === 'container') {
        this.$emit('sortDesc', this.isSortDirDesc)
      } else {
        this.openContainers.forEach(container => {
          this.requestParcels(container)
        })
      }
    },
  },
  mounted() {
    EventBus.$emit('handle-filters-visibility', false)
    // Restore the tree filter inputs from the URL and drive the (single) filtered
    // fetch. The parent page skips its initial fetch when URL tree filters exist
    // (see NotSent.created), so this is the only request in that case.
    const q = this.$route.query
    if (q.tracking_number) this.filterContainerCodesByTrackingNumber = q.tracking_number.split(',')
    if (q.container_code) this.filterContainerCodes = q.container_code.split(',')
    if (q.mrn) this.filterContainerCodesByMrn = q.mrn.split(',')
    if (q.lrn) this.filterContainerCodesByLrn = q.lrn.split(',')
    if (this.filtersApplied) this.fetchContainersData()
  },
  created() {
    EventBus.$on('update-parcel-items', this.handleUpdateParcelItems)
    EventBus.$on('clear-selections', this.tableClearSelections)
  },
  beforeDestroy() {
    EventBus.$off('update-parcel-items', this.handleUpdateParcelItems)
    EventBus.$off('clear-selections', this.tableClearSelections)
    EventBus.$emit('handle-filters-visibility', true)
  },
  methods: {
    truncatedDescriptions,
    truncatedList,
    parcelValue(item) {
      return resolveGoodsShipmentValue(item)
    },
    isFilterAllowed(filterName) {
      if (this.notAllowedFilters.length) {
        return !this.notAllowedFilters.includes(filterName)
      }
      return true
    },
    handleUpdateParcelItems() {
      this.parcels = {}
      this.selectedParcel = null
      this.openContainers = []
    },
    openParcelModal(data) {
      this.selectedParcel = data
      this.selectedDeclaration = data.low_value_declaration
      // Programmatically open the correct modal based on declaration type
      const modalId = this.declarationType === 'h1'
        ? `edit-h1-parcel-items-${this.modalUid}`
        : `edit-parcel-items-${this.modalUid}`
      this.$nextTick(() => {
        this.$bvModal.show(modalId)
      })
    },
    deleteParcel(parcel) {
      this.$emit('deleteParcel', parcel)
    },
    resetFilters() {
      this.resetSelections()
      this.buyerCountry = this.defaultBuyerCountry
      this.filterContainerCodes = []
      this.filterContainerCodesByCountry = []
      this.filterContainerCodesByTrackingNumber = []
      this.filterContainerCodesByMrn = []
      this.filterContainerCodesByLrn = []
      this.arrived = null
      this.localFilters = {}
      pushQueryIfChanged(this.$router, this.$route, {})
    },
    tableClearSelections() {
      this.selectedAll = true
      this.selectedAll = false
      this.parcels = {}
      this.openContainers = []
      this.selectedContainers = []
      this.filterContainerCodesByCountry = []
    },
    resetSelections() {
      this.tableClearSelections()
      this.propagateSelection()
    },
    applyCountryFilter() {
      this.resetSelections()

      if (this.buyerCountry !== this.defaultBuyerCountry && this.buyerCountryOptions.length > 1) {
        Object.keys(this.containersForView).forEach(containerKey => {
          const container = this.containersForView[containerKey]
          if (container.buyer_country === this.buyerCountry) {
            this.filterContainerCodesByCountry.push(containerKey)
          }
        })
      }
    },
    requestParcels(containerCode) {
      const parcelsUrl = this.declarationType === 'h1'
        ? `/v1/shipments/${this.shipment.id}/h1/containers/${containerCode}/goods-shipments`
        : `/v1/companies/${this.$activeCompany().data.company.id}/shipments/${this.shipment.id}/${this.declarationType}/containers/${containerCode}/parcels`
      this.$http.get(parcelsUrl, {
        params: {
          filter: this.$attrs.filter,
          tracking_number: this.filterContainerCodesByTrackingNumber.join(','),
          arrived: this.arrived,
          mrn: this.filterContainerCodesByMrn.join(','),
          lrn: this.filterContainerCodesByLrn.join(','),
          sortBy: this.sortBy !== 'container' ? this.sortBy : null,
          sortDesc: this.isSortDirDesc,
        },
      })
        .then(response => {
          this.loading[containerCode] = false
          const temp = this.parcels
          temp[containerCode] = response.data.data
          this.parcels = null
          this.parcels = temp
          this.fullParcelsData.push(temp)
          this.$emit('parcels', {
            fullParcelsData: this.fullParcelsData,
          })
          this.applyChangeContainer(containerCode)
        })
    },

    openCloseParcels(containerCode) {
      const openedIndex = this.openContainers.indexOf(containerCode)
      if (openedIndex === -1) {
        this.openContainers.push(containerCode)
      } else {
        this.openContainers.splice(openedIndex, 1)
      }
    },
    onChangeAll() {
      this.applyChangeAll()
      this.propagateSelection()
    },
    onChangeContainer(containerCode) {
      this.applyChangeContainer(containerCode)
      this.selectAllByChildren()
      this.propagateSelection()
    },
    onChangeParcel(containerCode) {
      this.applyChangeParcel(containerCode)
      this.selectAllByChildren()
      this.propagateSelection()
    },
    applyChangeAll() {
      const containers = this.containersForView

      Object.keys(containers).forEach(containerCode => {
        const containerIndex = this.selectedContainers.indexOf(containerCode)

        if (this.selectedAll) {
          if (
            !this.filterContainerCodesByCountry.length
            || this.filterContainerCodesByCountry.includes(containerCode)
          ) {
            // only missing containers should be added to the list
            if (containerIndex === -1) {
              this.selectedContainers.push(containerCode)
            }
          }
        } else if (containerIndex !== -1) {
          this.selectedContainers.splice(containerIndex, 1)
        }
        this.applyChangeContainer(containerCode, false)
      })
    },
    applyChangeContainer(containerCode) {
      const parcels = this.parcels[containerCode]

      if (typeof parcels === 'undefined') {
        return
      }

      Object.keys(parcels).forEach(parcelKey => {
        const trackingNumber = parcels[parcelKey].tracking_number
        const parcelIndex = this.selectedParcels.indexOf(trackingNumber)

        if (this.selectedContainers.indexOf(containerCode) !== -1) {
          if (parcelIndex === -1) {
            this.selectedParcels.push(trackingNumber)
          }
        } else if (parcelIndex !== -1) {
          this.selectedParcels.splice(parcelIndex, 1)
        }
      })
    },
    applyChangeParcel(containerCode) {
      const parcels = this.parcels[containerCode]

      let allContainerParcelsSelected = true

      Object.keys(parcels).every(parcelKey => {
        const oneTrackingNumber = parcels[parcelKey].tracking_number
        const parcelIndex = this.selectedParcels.indexOf(oneTrackingNumber)

        if (parcelIndex === -1 || this.filtersApplied) {
          allContainerParcelsSelected = false
          return false
        }

        return true
      })

      const selectedContainerIndex = this.selectedContainers.indexOf(containerCode)
      if (allContainerParcelsSelected) {
        if (selectedContainerIndex === -1) {
          this.selectedContainers.push(containerCode)
        }
      } else if (selectedContainerIndex !== -1) {
        this.selectedContainers.splice(selectedContainerIndex, 1)
      }
    },
    selectAllByChildren() {
      const containers = this.containersForView

      let allSelected = true

      Object.keys(containers).every(containerCode2 => {
        const containerIndex2 = this.selectedContainers.indexOf(containerCode2)
        if (containerIndex2 === -1) {
          allSelected = false
          return false
        }
        return true
      })

      this.selectedAll = allSelected
    },
    propagateSelection() {
      let allSelected = this.selectedAll
      if (
        this.buyerCountry !== this.defaultBuyerCountry && this.buyerCountryOptions.length > 2
      ) {
        allSelected = false
      }

      this.$emit('selection', {
        selectedAll: allSelected,
        selectedContainers: this.selectedContainers,
        selectedParcels: this.selectedParcels,
      })
    },
    isOpened(containerCode) {
      return this.openContainers.indexOf(containerCode) !== -1
    },
    fetchContainersData() {
      this.openContainers = []
      this.parcels = []
      this.localFilters = {
        container_codes: this.filterContainerCodes,
        tracking_number: this.filterContainerCodesByTrackingNumber,
        arrived: this.arrived,
        mrn: this.filterContainerCodesByMrn,
        lrn: this.filterContainerCodesByLrn,
      }
      const query = {}
      if (this.filterContainerCodesByTrackingNumber.length) query.tracking_number = this.filterContainerCodesByTrackingNumber.join(',')
      if (this.filterContainerCodes.length) query.container_code = this.filterContainerCodes.join(',')
      if (this.filterContainerCodesByMrn.length) query.mrn = this.filterContainerCodesByMrn.join(',')
      if (this.filterContainerCodesByLrn.length) query.lrn = this.filterContainerCodesByLrn.join(',')
      pushQueryIfChanged(this.$router, this.$route, query)
    },
  },
}
</script>
<style>
  #buyer-country {
    height: 2.4rem;
  }
  #filter-reset {
    padding-top: .63rem;
    padding-bottom: .63rem;
  }
  .chevron-icon {
    transition: all 0.3s;
  }
  .chevron-icon.expanded {
    transform: rotate(90deg);
  }
</style>
